import { GameState } from "./game-state";
import { Ball } from "./ball";
import { HitResultEnum } from "../types/hit-result-enum";
import { Game } from "./game";
import { BallTypeEnum } from "../types/ball-type-enum";
import { Vector2 } from "../math/vector2";
import { GameConfig } from "../config/game-config";
import { IAssetsConfig } from "../types/game-config-type";
import { Assets } from "./resources";
import { useStore } from "../stores";
import { BallsFactory } from "./balls-factory";
import gameApplication from "./game-app";
import { MutationType } from "../stores/mutation-types";
import { ChatMessage } from "../protocol/messages/chat-message";
import { WebsocketMessageType } from "../protocol/websocket-message-type";

const sounds: IAssetsConfig = GameConfig.sounds;

export class GamePractice extends Game {

  private eightBall: Ball;
  private nineBall: Ball;

  private shots: number = 1;
  private ballsPotted: number = 0;

  constructor() {
    super();
    this.initGame();
  }

  /**
   * This initilises the game. 
   */
  public initGame(): void {
    const store = useStore();
    
    this.rackBalls();

    this.shots = 1;
    this.ballsPotted = 0;
    
    // initilise the game state
    this.gameState = new GameState();
    this.gameState.setupCueBall = true;

    // start the game play
    this.startGame();
  }

  private rackBalls(): void {
    const store = useStore();

    // This will depend on the practice game type in the settings.
    if (store.state.settings.defaultPracticeGame === 'POOL8') {
      this.gameBalls = BallsFactory.createPool8Balls();
      this.eightBall = this.gameBalls[8];
    } else if (store.state.settings.defaultPracticeGame === 'POOL9') {
      this.gameBalls = BallsFactory.createPool9Balls();
      this.nineBall = this.gameBalls[9];
    } else if (store.state.settings.defaultPracticeGame === 'POOL8UK') {
      this.gameBalls = BallsFactory.createPool8UKBalls();
      this.eightBall = this.gameBalls[8];
    } else if (store.state.settings.defaultPracticeGame === 'SNOOKER') {
      this.gameBalls = BallsFactory.createSnookerBalls(false);
    } else if (store.state.settings.defaultPracticeGame === 'SNOOKER6') {
      this.gameBalls = BallsFactory.createSnookerBalls(true);
    }
    
    this.cueBall = this.gameBalls[0];
  }

  public update(): void {
    const store = useStore();
    const hitPositionSize = store.state.settings.hitPositionSize;

    if (!this.gameState.setupCueBall) {
      // Cue stick functionality is disabled if the mouse is on the hit position.
      // This is because the hit position functionalty interfears with
      // the cue stick functionaliy when enabled.
      if (this.stick.enabled && hitPositionSize == 'SMALL_BALL' ||
          this.stick.active && hitPositionSize == 'LARGE_BALL') {
        // Update the cue stick.
        this.handleStickInput();
        this.stick.update();
      }

       // Update the balls on the table.
      this.handleBallsInPocketsPractice();
      this.handleCollisions();
      this.gameBalls.forEach((ball: Ball) => ball.update());

      if (!this.isBallsMoving && !this.stick.visible) {
        // Conclude the turn when the ball have stopped moving.
        this.hitResult = this.processEvents();
        this.concludePracticeTurn(this.hitResult);
      }
    }

    if (this.gameState.setupCueBall && this.stick.active) {
      this.handleBallInHand();
    }
  }
  
  protected processEvents(): HitResultEnum { 
    const store = useStore();

    if (store.state.settings.defaultPracticeGame === 'POOL8' ||
      store.state.settings.defaultPracticeGame === 'POOL8UK') {
      // check if black 8 ball is potted
      if (!this.eightBall.onTable) {
        const chatMessage: ChatMessage = {
          msgType: WebsocketMessageType.CHAT_MSG,
          playerId: 'STATUS',
          message: 'Game ended - Eight ball is potted.',
        }

        store.commit(MutationType.CHAT_MESSAGE, chatMessage);
        return HitResultEnum.END;
      }
    } else if (store.state.settings.defaultPracticeGame === 'POOL9') {
      // check if 9 ball is potted
      if (!this.nineBall.onTable) {
        const chatMessage: ChatMessage = {
          msgType: WebsocketMessageType.CHAT_MSG,
          playerId: 'STATUS',
          message: 'Nine ball is potted.',
        }

        store.commit(MutationType.CHAT_MESSAGE, chatMessage);

        return HitResultEnum.END;
      }
    } else if (store.state.settings.defaultPracticeGame === 'SNOOKER' || 
            store.state.settings.defaultPracticeGame === 'SNOOKER6') {
      // see if all of any balls are on the table
      let ballCount: number = 0;

      for (let i: number = 1; i < this.balls.length; i++) {
        const b: Ball = this.balls[i];
        try {
          if (b.onTable) {
            // the ball is not the cue ball
            if (b.type !== BallTypeEnum.Cue) {
              ballCount++;
            }
          }
        } catch (err) {
          console.log("ERROR - there is a problem with the ball", i)
        }
      }

      if (ballCount === 0) {
        const chatMessage: ChatMessage = {
          msgType: WebsocketMessageType.CHAT_MSG,
          playerId: 'STATUS',
          message: 'All balls potted.',
        }

        store.commit(MutationType.CHAT_MESSAGE, chatMessage);

        return HitResultEnum.END;
      }
    }

    return HitResultEnum.OK;
  }

  /**
   * This is called after the player has taken a shot.
   */
  private concludePracticeTurn(hitResult: HitResultEnum): void { 

    const store = useStore();

    this.shots++;
         
    if (this.isGameOver) {

      const chatMessage: ChatMessage = {
        msgType: WebsocketMessageType.CHAT_MSG,
        playerId: 'STATUS',
        message: 'New game started.',
      }

      store.commit(MutationType.CHAT_MESSAGE, chatMessage);

      // Emit "end game" event to the Vue.js front end.
      gameApplication.fireEvent("END_GAME");

      // start another game
      this.initGame();
     
      return;
    }

    if (!this.cueBall.visible) {
      this.cueBall.show(Vector2.copy(GameConfig.cueBallPosition));

      const chatMessage: ChatMessage = {
        msgType: WebsocketMessageType.CHAT_MSG,
        playerId: 'STATUS',
        message: 'Scratched cue ball.',
      }

      store.commit(MutationType.CHAT_MESSAGE, chatMessage);

      this.isFoul = true;
    }

    const ballsInPocket = [
      ...this.gameState.ballsInPocket,
      ...this.gameState.pocketedBalls,
    ]; // this is used to add current "pocketedBalls" to the "ballsInPocket"

    this.gameState = new GameState(); // clear the game state after a player turn
    this.gameState.ballsInPocket = ballsInPocket; // keep the balls in pocket
    this.gameState.setupCueBall = this.isFoul;
    this.isFoul = false;
  
    // If we have cue ball in "ballsInPocket", remove it.
    this.gameState.removeCueBallFromPocket(BallTypeEnum.Cue);

    this.stick.show(this.cueBall.position);
    this.stick.movable = true;

    // Emit "end turn" event to the Vue.js front end.
    // For the other game types the game this is done 
    // when the game is syncronised and then continued.
    gameApplication.fireEvent("END_TURN");
  }

  /**
   * Validate the position of the cue ball on the table.
   * 
   * @param position - the position of the cue ball
	 * @returns Retruns "true" if the cue ball postion is valid and "false" of not
   */
  public validateCueBallPosition(position: Vector2): boolean {
    return true; // the cue ball can be placed anywhere on the table
  }

  /**
   * This handles the change the of the current player if necessary.
   * There is no player change on the practice game.
   * This method is required for inherentance.
   */
  public continueGame(): void {  
    // The body of this method is empty.
  }
  
  private handleBallsInPocketsPractice(): void {
    const store = useStore();

    this.gameBalls.forEach((ball: Ball) => {
      const isInPocket = this.tableEvents.isInsidePocket(ball.position);
      if (isInPocket && !this.gameState.pocketedBalls.includes(ball)) {
        Assets.playSound(sounds.paths.BALL_RAIL, 1);
        ball.shake(this.closestPocket(ball.position));

        this.gameState.pocketedBalls.push(ball);

        if (ball.type !== BallTypeEnum.Cue) { // don't start potted ball animation on the cue ball
          // this is use to help get the index for the pocketed balls bar
          const balls = [
            ...this.gameState.pocketedBalls,
            ...this.gameState.ballsInPocket,
          ].filter((b) => b.type !== BallTypeEnum.Cue);
          
          // Animate the ball in the pocket.
          setTimeout(() => {
            const index = balls.length - 1; // index for the pocketed balls bar
            ball.fall(index);
          }, 1000); // run after 1 second

          this.ballsPotted++;
          
          let potRate = (this.ballsPotted / this.shots) * 100;

          if (potRate > 100) {
            potRate = 100;
          }

          const chatMessage: ChatMessage = {
            msgType: WebsocketMessageType.CHAT_MSG,
            playerId: 'STATUS',
            message: `Ball potted. Shots: ${this.shots}, Pots: ${this.ballsPotted}, Pot Rate: ${Math.round(potRate)}%`,
          }

          store.commit(MutationType.CHAT_MESSAGE, chatMessage);
        }
      }
    });
  }
}

